// A VIDEO OF WHAT A PAGE DRAWS, recorded in the browser: a 2D canvas `frame` paints each frame
// into, captured as a stream and encoded by the browser's own MediaRecorder — MP4 where it can,
// WebM where not. In real time: the recording lasts as long as what it records plays.
const TYPES = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
const FPS = 30;

export type Recording = { stop: () => Promise<Blob>; ext: string };

// The type this browser records in, and the extension it is saved under.
const recordedType = () => TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
export const recordedExt = () => (recordedType().includes('mp4') ? 'mp4' : 'webm');

export function startRecording(width: number, height: number, frame: (ctx: CanvasRenderingContext2D) => void): Recording {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const type = recordedType();
  const recorder = new MediaRecorder(canvas.captureStream(FPS), type ? { mimeType: type, videoBitsPerSecond: 8_000_000 } : undefined);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  let raf = 0;
  const tick = () => { frame(ctx); raf = requestAnimationFrame(tick); };
  tick();
  recorder.start(1000);
  return {
    ext: recordedExt(),
    stop: () => new Promise((done) => {
      cancelAnimationFrame(raf);
      recorder.onstop = () => done(new Blob(chunks, { type: type || 'video/webm' }));
      recorder.stop();
    }),
  };
}

// HANDED TO THE READER as a file to save.
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// THE CAPTION BURNT IN, as the page draws it over the picture: a rounded white box at the foot,
// its beat number first, the sentence wrapped to the box.
export function drawCaption(ctx: CanvasRenderingContext2D, w: number, h: number, text: string, beat: string | null) {
  if (!text) return;
  const scale = w / 1280;
  const font = Math.round(22 * scale);
  ctx.font = `${font}px system-ui, sans-serif`;
  const label = beat ? `${beat}  |  ${text}` : text;
  const maxW = w * 0.7;
  const words = label.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxW && line) { lines.push(line); line = word; } else line = next;
  }
  if (line) lines.push(line);
  const lh = font * 1.35;
  const boxW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 40 * scale;
  const boxH = lines.length * lh + 20 * scale;
  const x = (w - boxW) / 2;
  const y = h - boxH - 24 * scale;
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.strokeStyle = '#1e40af';
  ctx.lineWidth = 2 * scale;
  ctx.beginPath();
  ctx.roundRect(x, y, boxW, boxH, 16 * scale);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#1e3a8a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((l, i) => ctx.fillText(l, w / 2, y + 10 * scale + lh * (i + 0.5)));
}
